/**
 * Spanish orthography: syllabification, the written-accent rules, ñ, and the inverted marks
 * (BRIEF <spanish_correctness> layer 2, SPEC §2 `lang/accents.ts`).
 *
 * Two jobs that must not be confused:
 *
 *  1. **Normalisation for scoring.** Accent- and case-insensitive, so a missing accent never
 *     costs the learner the item outright. Used only to decide *whether* an answer matches.
 *  2. **Explaining the accent.** Which syllable is stressed, what class the word is, and why it
 *     does or does not carry a tilde. Used in feedback, which always shows the correctly accented
 *     form and names the error (invariant: feedback never just says "wrong").
 *
 * Pure. No I/O, no clock, no randomness.
 *
 * **ñ IS NEVER STRIPPED.** It is a distinct letter of the alphabet, not an n wearing a diacritic:
 * `año` and `ano` are different words, and folding them together would both mis-grade the item and
 * produce a genuinely unpleasant suggestion. `ü` *is* folded for matching, because there the
 * diaeresis really is a diacritic on u — and its absence is reported as an accent error rather
 * than silently accepted.
 */

/** The five vowels, plus the accented and diaeresis forms. `ñ` is a consonant and is not here. */
const UNACCENTED_VOWELS = 'aeiou';
const ACCENTED_VOWELS = 'áéíóú';
const DIAERESIS_VOWELS = 'üï';
const ALL_VOWELS = UNACCENTED_VOWELS + ACCENTED_VOWELS + DIAERESIS_VOWELS;

/** a, e, o — and their accented forms. Two strong vowels never share a syllable. */
const STRONG_VOWELS = 'aeoáéó';
/**
 * i, u — including the accented and diaeresis forms.
 *
 * The accented ones have to be in here. `sharesNucleus` asks "is this a weak vowel carrying a
 * written accent?" to detect a hiatus, and with `í` and `ú` missing from this set that question
 * was always false: `día` came back as one syllable. Found by the T29 tests.
 */
const WEAK_VOWELS = 'iuíúüï';

const ACCENT_TO_PLAIN: Readonly<Record<string, string>> = {
  á: 'a',
  é: 'e',
  í: 'i',
  ó: 'o',
  ú: 'u',
  ü: 'u',
  ï: 'i',
  Á: 'A',
  É: 'E',
  Í: 'I',
  Ó: 'O',
  Ú: 'U',
  Ü: 'U',
  Ï: 'I',
};

const PLAIN_TO_ACCENT: Readonly<Record<string, string>> = {
  a: 'á',
  e: 'é',
  i: 'í',
  o: 'ó',
  u: 'ú',
};

/** Consonant pairs that never split across a syllable boundary. */
const INSEPARABLE = new Set([
  'pr', 'br', 'tr', 'dr', 'cr', 'gr', 'fr',
  'pl', 'bl', 'cl', 'gl', 'fl',
]);

/** Digraphs: one sound, one letter for syllabification purposes. */
const DIGRAPHS = new Set(['ch', 'll', 'rr']);

export function isVowel(ch: string): boolean {
  return ALL_VOWELS.includes(ch.toLowerCase());
}

export function isStrongVowel(ch: string): boolean {
  return STRONG_VOWELS.includes(ch.toLowerCase());
}

export function isWeakVowel(ch: string): boolean {
  return WEAK_VOWELS.includes(ch.toLowerCase());
}

export function hasWrittenAccent(ch: string): boolean {
  return ACCENTED_VOWELS.includes(ch.toLowerCase());
}

// ---------------------------------------------------------------------------------------------
// Normalisation
// ---------------------------------------------------------------------------------------------

/**
 * Remove written accents and the diaeresis, preserving ñ.
 *
 * Deliberately a lookup table rather than `normalize('NFD').replace(/\p{M}/gu, '')`: the Unicode
 * decomposition route strips the tilde off ñ as well, which is the one thing that must never
 * happen here.
 */
export function stripAccents(text: string): string {
  let out = '';
  for (const ch of text) out += ACCENT_TO_PLAIN[ch] ?? ch;
  return out;
}

/** Put the written accent back on a plain vowel. Used to build the corrected form for feedback. */
export function addAccent(vowel: string): string {
  return PLAIN_TO_ACCENT[vowel.toLowerCase()] ?? vowel;
}

/**
 * The form two answers are compared as, and nothing else.
 *
 * Lower-cased, accent-folded, punctuation-stripped, whitespace-collapsed. SCORING ONLY: never show
 * this to the learner and never store it as the answer. The correctly accented form is what
 * feedback displays (BRIEF <spanish_correctness>).
 */
export function normalizeForScoring(text: string): string {
  return stripAccents(text)
    .toLowerCase()
    .replace(/[¿?¡!.,;:"'()]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Do two answers match once accents and case are set aside? */
export function matchesIgnoringAccents(expected: string, actual: string): boolean {
  return normalizeForScoring(expected) === normalizeForScoring(actual);
}

/** Exact, including accents and the inverted marks. Case and surrounding space still ignored. */
export function matchesExactly(expected: string, actual: string): boolean {
  return (
    expected.toLowerCase().replace(/\s+/g, ' ').trim() ===
    actual.toLowerCase().replace(/\s+/g, ' ').trim()
  );
}

// ---------------------------------------------------------------------------------------------
// Syllabification
// ---------------------------------------------------------------------------------------------

/**
 * Split a word into syllables.
 *
 * Nuclei first, then the consonants between them, which is the only ordering that gets the
 * diphthong/hiatus distinction right without a lookahead mess:
 *
 *  - two strong vowels are a hiatus (`le-er`, `ca-os`);
 *  - a written-accented weak vowel breaks a diphthong (`dí-a`, `ra-íz`, `pa-ís`);
 *  - strong + unaccented weak, or weak + weak, is one nucleus (`vien-to`, `ciu-dad`);
 *  - `h` between vowels is transparent (`ahu-mar`), because it is silent;
 *  - a single consonant joins the following syllable, two split unless they are inseparable or a
 *    digraph, three give one back and two forward, four split evenly.
 *
 * Words with no vowel at all come back as a single syllable rather than throwing: content is
 * linted separately, and a grading path is not the place to discover a typo in the corpus.
 */
export function syllabify(word: string): string[] {
  const chars = [...word];
  if (chars.length === 0) return [];

  // 1. Nucleus spans: [start, endExclusive) over the character array.
  const nuclei: Array<{ start: number; end: number }> = [];
  let i = 0;
  while (i < chars.length) {
    if (!isVowel(chars[i]!)) {
      i += 1;
      continue;
    }
    const start = i;
    let end = i + 1;
    while (end < chars.length) {
      // `h` is silent: it neither joins a nucleus nor separates one, so look past it.
      const next = chars[end] === 'h' || chars[end] === 'H' ? end + 1 : end;
      if (next >= chars.length || !isVowel(chars[next]!)) break;
      const previous = chars[end - 1]!;
      if (!sharesNucleus(previous, chars[next]!)) break;
      end = next + 1;
    }
    nuclei.push({ start, end });
    i = end;
  }

  if (nuclei.length === 0) return [word];

  // 2. Cut points between consecutive nuclei.
  const cuts: number[] = [];
  for (let n = 0; n < nuclei.length - 1; n += 1) {
    const from = nuclei[n]!.end;
    const to = nuclei[n + 1]!.start;
    cuts.push(from + consonantSplit(chars.slice(from, to)));
  }

  const out: string[] = [];
  let previous = 0;
  for (const cut of cuts) {
    out.push(chars.slice(previous, cut).join(''));
    previous = cut;
  }
  out.push(chars.slice(previous).join(''));
  return out.filter((s) => s.length > 0);
}

/** Do two adjacent vowels belong to the same syllable? */
function sharesNucleus(a: string, b: string): boolean {
  // A written accent on a weak vowel forces a hiatus: `dí-a`, `ra-íz`.
  if (hasWrittenAccent(a) && isWeakVowel(a)) return false;
  if (hasWrittenAccent(b) && isWeakVowel(b)) return false;
  // Two strong vowels are always separate syllables.
  if (isStrongVowel(a) && isStrongVowel(b)) return false;
  return true;
}

/** Where to cut a run of consonants between two nuclei, as an offset into that run. */
function consonantSplit(cluster: string[]): number {
  const n = cluster.length;
  if (n === 0) return 0;
  if (n === 1) return 0; // `ca-sa` — the consonant opens the next syllable.

  const pairAt = (index: number): string =>
    (cluster[index] ?? '').toLowerCase() + (cluster[index + 1] ?? '').toLowerCase();

  if (n === 2) {
    // `pe-rro`, `mu-cho`, `ha-blar` — digraphs and inseparable pairs travel together.
    return DIGRAPHS.has(pairAt(0)) || INSEPARABLE.has(pairAt(0)) ? 0 : 1;
  }
  if (n === 3) {
    // `ins-tante` unless the last two are inseparable: `im-presión` -> `im-pre-sión`.
    return DIGRAPHS.has(pairAt(1)) || INSEPARABLE.has(pairAt(1)) ? 1 : 2;
  }
  return 2;
}

// ---------------------------------------------------------------------------------------------
// Stress
// ---------------------------------------------------------------------------------------------

export type StressClass = 'aguda' | 'llana' | 'esdrujula' | 'sobresdrujula' | 'monosilaba';

export interface StressAnalysis {
  word: string;
  syllables: string[];
  /** 0-based index of the stressed syllable. */
  stressIndex: number;
  /** Counting from the end: 0 = last, 1 = penultimate, 2 = antepenultimate. */
  fromEnd: number;
  stressClass: StressClass;
  /** The word as written carries a tilde somewhere. */
  written: boolean;
  /** The rules require a tilde for this stress placement. */
  required: boolean;
  /** Feedback text: why the accent is or is not there. Plain, never scolding. */
  explanation: string;
}

/**
 * A word ending in a vowel, `n`, or `s` is stressed on the penultimate syllable by default;
 * anything else is stressed on the last. `y` counts as a consonant here — `rey` and `estoy` are
 * agudas and take no tilde.
 */
function endsInVowelNorS(word: string): boolean {
  const last = stripAccents(word).toLowerCase().at(-1);
  if (last === undefined) return false;
  return UNACCENTED_VOWELS.includes(last) || last === 'n' || last === 's';
}

/**
 * Where the stress falls and whether the written accent is consistent with it.
 *
 * With a tilde present, the tilde decides. Without one, the default rules decide — which means
 * this reports where the stress *would* be, not where a dictionary says it is. That limitation is
 * deliberate: everything downstream compares against a known-correct expected form from the
 * corpus, so no dictionary is needed, and no claim is made that this can pronounce an unknown word.
 */
export function analyseStress(word: string): StressAnalysis {
  const syllables = syllabify(word);
  const count = syllables.length;

  const accentedSyllable = syllables.findIndex((syllable) =>
    [...syllable].some((ch) => hasWrittenAccent(ch)),
  );
  const written = accentedSyllable >= 0;

  const stressIndex = written
    ? accentedSyllable
    : count <= 1
      ? 0
      : endsInVowelNorS(word)
        ? count - 2
        : count - 1;

  const fromEnd = count - 1 - stressIndex;
  const stressClass: StressClass =
    count <= 1
      ? 'monosilaba'
      : fromEnd === 0
        ? 'aguda'
        : fromEnd === 1
          ? 'llana'
          : fromEnd === 2
            ? 'esdrujula'
            : 'sobresdrujula';

  const required = requiresTilde(word, stressClass);

  return {
    word,
    syllables,
    stressIndex,
    fromEnd,
    stressClass,
    written,
    required,
    explanation: explain(stressClass, required),
  };
}

/** The three rules, as rules rather than as a lookup. */
function requiresTilde(word: string, stressClass: StressClass): boolean {
  switch (stressClass) {
    case 'esdrujula':
    case 'sobresdrujula':
      // Always. No exceptions, which is why it is the one rule worth memorising.
      return true;
    case 'aguda':
      return endsInVowelNorS(word);
    case 'llana':
      return !endsInVowelNorS(word);
    case 'monosilaba':
      // Monosyllables take no tilde except to separate homographs (tú/tu, él/el, sí/si). That is a
      // per-word fact, not a rule, so it is not decided here.
      return false;
  }
}

function explain(stressClass: StressClass, required: boolean): string {
  switch (stressClass) {
    case 'esdrujula':
      return 'Esdrújula — stress on the third-from-last syllable, so it always takes a tilde.';
    case 'sobresdrujula':
      return 'Sobresdrújula — stress before the third-from-last syllable, so it always takes a tilde.';
    case 'aguda':
      return required
        ? 'Aguda ending in a vowel, n or s — stress on the last syllable, so it takes a tilde.'
        : 'Aguda — stress on the last syllable, and it does not end in a vowel, n or s, so no tilde.';
    case 'llana':
      return required
        ? 'Llana not ending in a vowel, n or s — stress on the second-from-last syllable, so it takes a tilde.'
        : 'Llana — stress on the second-from-last syllable, and it ends in a vowel, n or s, so no tilde.';
    case 'monosilaba':
      return 'One syllable — no tilde, unless it is the kind that separates two words (tú/tu, él/el).';
  }
}

/**
 * Write the tilde onto the nucleus of a given syllable, removing any tilde already in the word.
 *
 * Needed because pluralisation adds a syllable without moving the stress, so a word can gain or
 * lose its tilde purely from the count changing: `joven` → `jóvenes` gains one, `canción` →
 * `canciones` loses one. Rather than tabulate that, the caller re-derives it from the rules.
 *
 * The tilde goes on the strong vowel of a diphthong (`ción` → the `o`), or on the last vowel when
 * they are all weak.
 */
export function accentSyllable(word: string, syllableIndex: number): string {
  const syllables = syllabify(stripAccents(word));
  const target = syllables[syllableIndex];
  if (target === undefined) return word;

  const chars = [...target];
  let position = -1;
  for (let i = 0; i < chars.length; i += 1) {
    if (!isVowel(chars[i]!)) continue;
    if (position === -1 || isStrongVowel(chars[i]!)) position = i;
    if (isStrongVowel(chars[i]!)) break;
  }
  if (position === -1) return word;

  chars[position] = addAccent(chars[position]!);
  syllables[syllableIndex] = chars.join('');
  return syllables.join('');
}

// ---------------------------------------------------------------------------------------------
// Accent comparison
// ---------------------------------------------------------------------------------------------

export type AccentComparison =
  /** Identical once case is set aside. */
  | 'match'
  /** Right letters, but a tilde the expected form has is absent. */
  | 'missing-accent'
  /** Right letters, but a tilde is in the wrong place or one appears that should not. */
  | 'wrong-accent'
  /** Not the same letters at all — not an accent question. */
  | 'different-word';

/**
 * Compare two forms purely on accentuation.
 *
 * Only ever called once the letters are known to match; `different-word` exists so the caller
 * cannot mistake an unrelated answer for an accent slip.
 */
export function compareAccents(expected: string, actual: string): AccentComparison {
  if (!matchesIgnoringAccents(expected, actual)) return 'different-word';

  /*
   * Punctuation is removed before the accents are looked at.
   *
   * `matchesExactly` keeps punctuation, so `Ven acá, mi amor.` and `Ven acá mi amor` are not
   * "exact" — and this function used to conclude from that pair, which carries the same accent in
   * the same place, that the accent was misplaced. The learner had spelled it perfectly and was
   * told his accent was wrong. Scoring already ignores punctuation (`normalizeForScoring`), because
   * nobody types a comma and a full stop with one thumb at 3:40 a.m.; the accent comparison has to
   * ignore it too or it is comparing two different questions. See DECISIONS D-176.
   */
  const wanted = withoutPunctuation(expected);
  const given = withoutPunctuation(actual);
  if (matchesExactly(wanted, given)) return 'match';

  const expectedMarks = countAccents(wanted);
  const actualMarks = countAccents(given);
  // No tilde at all where one was wanted is a different mistake from putting one in the wrong
  // place: the first is usually the keyboard, the second is usually the rule.
  return actualMarks === 0 && expectedMarks > 0 ? 'missing-accent' : 'wrong-accent';
}

/** Punctuation gone, accents and ñ kept. The form the accent question is actually about. */
function withoutPunctuation(text: string): string {
  return text.replace(/[¿?¡!.,;:"'()]/g, '').replace(/\s+/g, ' ').trim();
}

function countAccents(text: string): number {
  let count = 0;
  for (const ch of text) if (hasWrittenAccent(ch) || ch.toLowerCase() === 'ü') count += 1;
  return count;
}

// ---------------------------------------------------------------------------------------------
// Inverted punctuation
// ---------------------------------------------------------------------------------------------

export interface InvertedPunctuationCheck {
  ok: boolean;
  /** Marks the sentence should open with and does not. */
  missing: Array<'¿' | '¡'>;
}

/**
 * A sentence ending in `?` must open with `¿`, and one ending in `!` must open with `¡`.
 *
 * Checked on the segment that actually carries the final mark rather than on the whole string, so
 * `Hola, ¿cómo estás?` passes: the opening mark belongs where the question starts, not at the
 * front of the line.
 */
export function checkInvertedPunctuation(text: string): InvertedPunctuationCheck {
  const trimmed = text.trim();
  const missing: Array<'¿' | '¡'> = [];

  for (const [closing, opening] of [
    ['?', '¿'],
    ['!', '¡'],
  ] as const) {
    if (!trimmed.endsWith(closing)) continue;
    // Everything after the last sentence break is the clause the mark closes.
    const clause = trimmed.slice(0, -1).split(/[.;]|(?<=[?!])/).at(-1) ?? '';
    if (!clause.includes(opening)) missing.push(opening);
  }

  return { ok: missing.length === 0, missing };
}
